/**
 * Theme preference: System (default), Light or Dark, remembered per browser.
 *
 * Dark mode is on when `<html data-theme="dark">`; the token sheet
 * (`@nt/tokens/theme.css`) redefines every colour role under that attribute.
 * `index.html` carries an inline boot script that reads THEME_STORAGE_KEY and
 * sets the attribute before first paint (pinned by `theme.test.ts`), so the key
 * lives here and in that script only.
 *
 * The logic is a controller over injected storage / media query / root, so it
 * runs under this app's DOM-less test runtime. `useThemePreference` binds it to
 * the real browser globals.
 */
import { useCallback, useSyncExternalStore } from "react";

export type ThemePreference = "system" | "light" | "dark";
export type ResolvedTheme = "light" | "dark";

export const THEME_STORAGE_KEY = "nt-admin-theme";
export const THEME_PREFERENCES: readonly ThemePreference[] = [
  "system",
  "light",
  "dark",
];

const DARK_QUERY = "(prefers-color-scheme: dark)";

interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}
interface MediaLike {
  matches: boolean;
  addEventListener(
    type: "change",
    fn: (event: { matches: boolean }) => void,
  ): void;
  removeEventListener(
    type: "change",
    fn: (event: { matches: boolean }) => void,
  ): void;
}
interface RootLike {
  dataset: Record<string, string | undefined>;
  style: { colorScheme: string };
}
export interface ThemeEnv {
  storage: StorageLike | null;
  media: MediaLike | null;
  root: RootLike | null;
  /** Pause CSS transitions for a flip; returns the release. Without it, every
   * `transition-[background-color]` fades between themes for 150ms. */
  freezeTransitions?: () => () => void;
}

function isPreference(value: unknown): value is ThemePreference {
  return value === "system" || value === "light" || value === "dark";
}

export function readPreference(storage: StorageLike | null): ThemePreference {
  try {
    const value = storage?.getItem(THEME_STORAGE_KEY);
    return isPreference(value) ? value : "system";
  } catch {
    return "system";
  }
}

export function writePreference(
  preference: ThemePreference,
  storage: StorageLike | null,
): void {
  try {
    storage?.setItem(THEME_STORAGE_KEY, preference);
  } catch {
    // Private mode or blocked storage: the choice just won't persist.
  }
}

export function resolveTheme(
  preference: ThemePreference,
  prefersDark: boolean,
): ResolvedTheme {
  if (preference === "system") return prefersDark ? "dark" : "light";
  return preference;
}

export function applyTheme(
  resolved: ResolvedTheme,
  root: RootLike | null,
): void {
  if (!root) return;
  root.dataset.theme = resolved;
  root.style.colorScheme = resolved;
}

export function createThemeController(env: ThemeEnv) {
  let preference = readPreference(env.storage);
  const listeners = new Set<() => void>();

  let applied: ResolvedTheme | undefined;
  const apply = () => {
    const next = resolveTheme(preference, env.media?.matches ?? false);
    const release =
      applied !== undefined && applied !== next
        ? env.freezeTransitions?.()
        : undefined;
    applyTheme(next, env.root);
    applied = next;
    release?.();
  };
  const onSystemChange = () => {
    if (preference === "system") apply();
  };
  apply();

  return {
    getPreference: () => preference,
    setPreference(next: ThemePreference) {
      preference = next;
      writePreference(next, env.storage);
      apply();
      for (const listener of [...listeners]) listener();
    },
    subscribe(listener: () => void) {
      if (listeners.size === 0) {
        // Re-sync: the system may have changed while nobody was listening.
        apply();
        env.media?.addEventListener("change", onSystemChange);
      }
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
        if (listeners.size === 0) {
          env.media?.removeEventListener("change", onSystemChange);
        }
      };
    },
  };
}

function browserEnv(): ThemeEnv {
  if (typeof window === "undefined") {
    return { storage: null, media: null, root: null };
  }
  let storage: StorageLike | null = null;
  try {
    storage = window.localStorage;
  } catch {
    storage = null;
  }
  return {
    storage,
    media: window.matchMedia ? window.matchMedia(DARK_QUERY) : null,
    root: document.documentElement,
    freezeTransitions: () => {
      const style = document.createElement("style");
      style.textContent = "*,*::before,*::after{transition:none!important}";
      document.head.appendChild(style);
      return () => {
        // Force a style recalc with transitions off, then let them back on.
        void getComputedStyle(document.body).opacity;
        requestAnimationFrame(() => style.remove());
      };
    },
  };
}

let shared: ReturnType<typeof createThemeController> | undefined;
function controller() {
  shared ??= createThemeController(browserEnv());
  return shared;
}

/**
 * Follow the operating system for the whole session, not only while a switch
 * is mounted: on a phone the switch lives in a closed menu. Called once from
 * main.tsx; the boot script in index.html has already painted the first frame.
 */
export function startThemeSync(): () => void {
  return controller().subscribe(() => {});
}

/** `[preference, setPreference]`; applies on change and follows the OS while "system". */
export function useThemePreference(): readonly [
  ThemePreference,
  (next: ThemePreference) => void,
] {
  const preference = useSyncExternalStore(
    (listener) => controller().subscribe(listener),
    () => controller().getPreference(),
    () => "system" as const,
  );
  const setPreference = useCallback(
    (next: ThemePreference) => controller().setPreference(next),
    [],
  );
  return [preference, setPreference] as const;
}
